/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { type tokens } from '@neo4j-ndl/base';
import type React from 'react';

import type { Tooltip } from '../tooltip';

export type HtmlAttributes<T extends React.ElementType> =
  React.ComponentPropsWithoutRef<T> & {
    [key: `data-${string}`]: string | undefined;
  };

type BaseProps<T extends React.ElementType> = {
  /**
   * Additional classnames will be applied to the root element.
   */
  className?: string;
  /**
   * Additional css styling. Will be applied to the root element.
   */
  style?: React.CSSProperties;

  /**
   * Html attributes to apply to the root element. This will override any default html attributes, use with caution.
   */
  htmlAttributes?: HtmlAttributes<T>;

  /**
   * A ref to apply to the root element.
   */
  ref?: React.ComponentPropsWithRef<T>['ref'];
};

export type PolymorphicCommonProps<T extends React.ElementType, P> = P & {
  /**
   * An override of the default HTML tag of the root of the component.
   * Can also be another React component.
   */
  as?: T;
} & BaseProps<T>;

export type CommonProps<T extends React.ElementType, P> = P & BaseProps<T>;

export type TooltipObjectProps = {
  root?: Partial<React.ComponentProps<typeof Tooltip>>;
  trigger?: Partial<React.ComponentProps<typeof Tooltip.Trigger>>;
  content?: Partial<React.ComponentProps<typeof Tooltip.Content>>;
};

export type Spacing = keyof typeof tokens.space;

export type Radius = keyof typeof tokens.borderRadius;

/**
 * Currently supporting only native HTML elements.
 */
export type CustomElementType = keyof HTMLElementTagNameMap;

/**
 * More info here:
 * https://stackoverflow.com/questions/75085783/conditional-react-component-properties-with-multiple-generics#comment132505139_75086121
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type ReactComponent = (args: any) => React.JSX.Element;
